import { useState, useEffect, useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import SupportMentorsListing from 'src/supportPortal/components/common/mentor/listing/SupportMentorsListing'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { styled } from '@mui/material/styles'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const TabsContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(216, 101, 0, 0.08) 0%, rgba(216, 101, 0, 0.12) 100%)',
  padding: theme.spacing(2),
  borderRadius: '0 0 16px 16px',
  borderBottom: '1px solid rgba(216, 101, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: '0 0 12px 12px',
  },
}))

function normalizeTargetSpecialties(resp) {
  if (!resp || resp.status !== 200) return []
  let list = []
  if (Array.isArray(resp.data)) {
    list = resp.data
  } else if (isCommonResponseSuccessfulV2(resp)) {
    const data = getDataMapFromCommonResponse(resp)
    if (Array.isArray(data)) list = data
    else if (data?.content && Array.isArray(data.content)) list = data.content
    else if (data?.targetSpecialties && Array.isArray(data.targetSpecialties)) list = data.targetSpecialties
    else if (data && typeof data === 'object') {
      const key = Object.keys(data).find((k) => Array.isArray(data[k]))
      if (key) list = data[key]
    }
  }
  if (list.length === 0 && resp.data?.content) list = resp.data.content || []
  if (list.length === 0 && resp.data?.data) list = Array.isArray(resp.data.data) ? resp.data.data : []
  return Array.isArray(list) ? list : []
}

export default function MentorBookings() {
  const [selectedTab, setSelectedTab] = useState('clinical')
  const [targetSpecialties, setTargetSpecialties] = useState([])
  const theme = useTheme()

  useEffect(() => {
    let cancelled = false
    async function fetchTargetSpecialties() {
      const resp = await getSPDataWithParams('/api/secure/cc/support/target-specialties/page', {
        includeDisabled: false,
        page: 0,
        size: 200,
      })
      if (cancelled) return
      setTargetSpecialties(normalizeTargetSpecialties(resp))
    }
    fetchTargetSpecialties()
    return () => { cancelled = true }
  }, [])

  const tabs = useMemo(() => {
    const base = [
      { id: 'all', label: 'All' },
      { id: 'clinical', label: 'Clinical' },
    ]
    const screenerBySpecialty = (targetSpecialties || []).map((s) => ({
      id: `screener-${s.slug || s.id || s.title}`,
      label: `Screeners – ${s.title || s.name || s.slug || 'Unknown'}`,
      slug: s.slug || s.id,
    }))
    const noSpecialty = { id: 'screener-no-specialty', label: 'Screeners – No specialty', slug: null }
    return [...base, ...screenerBySpecialty, noSpecialty]
  }, [targetSpecialties])

  const isScreenerTab = selectedTab.startsWith('screener-')
  const screenerSpecialtySlug = useMemo(() => {
    if (selectedTab === 'screener-no-specialty') return null
    if (!isScreenerTab) return undefined
    const tab = tabs.find((t) => t.id === selectedTab)
    return tab?.slug
  }, [selectedTab, isScreenerTab, tabs])

  let mentorsListApiSignature = '/api/secure/cc/support/therapists/clinical/list'
  let listApiParams = {}
  if (selectedTab === 'all') {
    mentorsListApiSignature = '/api/secure/cc/support/therapists/list'
  } else if (isScreenerTab) {
    mentorsListApiSignature = '/api/secure/cc/support/screeners/clinical/list'
    if (screenerSpecialtySlug !== undefined) {
      if (screenerSpecialtySlug != null) listApiParams = { speciality: screenerSpecialtySlug }
    }
  }

  return (
    <>
      <SupportHeader heading="Therapists" showBackButton />
      <TabsContainer>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
          <BaseUITabsComponent
            variant="contained"
            tabs={tabs}
            value={selectedTab}
            unselectedColor="rgba(71, 85, 105, 0.85)"
            selectedColor="rgba(216, 101, 0, 0.95)"
            hoverItemColor="rgba(216, 101, 0, 0.9)"
            onChange={setSelectedTab}
            getTabId={(tab) => tab.id}
            getTabLabel={(tab) => tab.label}
            incicatorColor="primary"
          />
        </Box>
      </TabsContainer>
      <SupportMentorsListing
        mentorsListApiSignature={mentorsListApiSignature}
        listApiParams={listApiParams}
        variant={isScreenerTab ? 'screener' : 'therapist'}
        heading="Therapists"
        exculudeMenuItemsList={[]}
        hideHeading
        screenerNoSpecialtyFilter={selectedTab === 'screener-no-specialty'}
      />
    </>
  )
}
